export const GRID_SIZE = 40;
/** @deprecated Prefer passing gridRows; kept as default for stadium canvas. */
export const GRID_ROWS = 60;
export const DEFAULT_GRID_COLS = 80;
export const MAX_GRID_COLS = 400;
export const MAX_GRID_ROWS = 300;
export const MIN_GRID_COLS = 10;
export const MIN_GRID_ROWS = 10;

export const seatTypes = [
  { value: 'REGULAR', label: 'Koltuk', color: '#AE256D' },
  { value: 'VIP', label: 'VIP', color: '#F59E0B' },
  { value: 'WHEELCHAIR', label: 'Engelli', color: '#3B82F6' },
  { value: 'COMPANION', label: 'Refakatçi', color: '#8B5CF6' },
];

export const ZONE_PRESETS = [
  { id: 'vip', name: 'VIP', color: '#F59E0B', capacity: '' },
  { id: 'tribune', name: 'Tribün', color: '#3B82F6', capacity: '' },
  { id: 'general', name: 'Genel', color: '#10B981', capacity: '' },
  { id: 'pitchside', name: 'Sahaya Yakın', color: '#EF4444', capacity: '' },
  { id: 'balcony', name: 'Balkon', color: '#8B5CF6', capacity: '' },
  { id: 'box', name: 'Localar', color: '#EC4899', capacity: '' },
];

export const getSeatTypeInfo = (seatType) => {
  return seatTypes.find((type) => type.value === seatType) || seatTypes[0];
};

/** Excel-style row labels: 0→A … 25→Z, 26→AA … */
export const rowIndexToLabel = (rowIndex) => {
  if (rowIndex == null || rowIndex < 0 || !Number.isFinite(rowIndex)) return '?';
  let n = Math.floor(rowIndex);
  let label = '';
  do {
    label = String.fromCharCode(65 + (n % 26)) + label;
    n = Math.floor(n / 26) - 1;
  } while (n >= 0);
  return label;
};

/** "A"→0, "E"→4, "AA"→26 */
export const labelToRowIndex = (label) => {
  if (!label || typeof label !== 'string') return 0;
  const s = label.trim().toUpperCase();
  if (!/^[A-Z]+$/.test(s)) return 0;
  let n = 0;
  for (let i = 0; i < s.length; i += 1) {
    n = n * 26 + (s.charCodeAt(i) - 64);
  }
  return n - 1;
};

export const DEFAULT_NAMING = {
  rowStart: 'A',
  colStart: 1,
  aisleCols: [],
  /** Per-cell corridors: "row-col" — does not kill the whole column */
  aisleCells: [],
  voidCols: [],
  voidRows: [],
};

export const cellKey = (row, col) => `${Number(row)}-${Number(col)}`;

export const isAisleCell = (row, col, naming = DEFAULT_NAMING) =>
  (naming.aisleCells || []).includes(cellKey(row, col));

/** Whole-column aisle or void (blocks the entire column). */
export const isSkippedCol = (col, naming = DEFAULT_NAMING) => {
  const c = Number(col);
  return (naming.aisleCols || []).includes(c) || (naming.voidCols || []).includes(c);
};

export const isSkippedRow = (row, naming = DEFAULT_NAMING) =>
  (naming.voidRows || []).includes(Number(row));

/** Cell cannot hold a seat (void col/row, full-col aisle, or cell aisle). */
export const isSkippedCell = (row, col, naming = DEFAULT_NAMING) =>
  isSkippedCol(col, naming) || isSkippedRow(row, naming) || isAisleCell(row, col, naming);

export const getRowLabel = (row, naming = DEFAULT_NAMING) => {
  const start = labelToRowIndex(naming.rowStart || 'A');
  const r = Number(row);
  // Void rows are skipped in letter sequence — like aisle cols skip seat numbers.
  // Row indices 0,1,2,void,4 → labels A,B,C,(void),D (not E).
  let count = 0;
  for (let i = 0; i <= r; i += 1) {
    if (!isSkippedRow(i, naming)) count += 1;
  }
  if (isSkippedRow(r, naming)) {
    return `${rowIndexToLabel(start + count)}·`;
  }
  return rowIndexToLabel(start + count - 1);
};

/** Display label for row header axis (includes void marker). */
export const getRowHeaderLabel = (row, naming = DEFAULT_NAMING) => {
  if (isSkippedRow(row, naming)) {
    const start = labelToRowIndex(naming.rowStart || 'A');
    let count = 0;
    for (let i = 0; i < Number(row); i += 1) {
      if (!isSkippedRow(i, naming)) count += 1;
    }
    return `${rowIndexToLabel(start + count)}·`;
  }
  return getRowLabel(row, naming);
};

/**
 * Seat number for a grid column.
 * With `row`, per-cell aisles in that row are skipped (B9 · gap · B10).
 * Without `row`, only whole-column aisle/void skips.
 */
export const getSeatNumberForCol = (col, naming = DEFAULT_NAMING, row = null) => {
  const start = Number(naming.colStart) || 1;
  const cMax = Number(col);
  let count = 0;
  for (let c = 0; c <= cMax; c += 1) {
    if ((naming.voidCols || []).includes(c) || (naming.aisleCols || []).includes(c)) {
      continue;
    }
    if (row != null && isAisleCell(row, c, naming)) continue;
    count += 1;
  }
  return start + count - 1;
};

export const formatSeatLabel = (row, col, naming = DEFAULT_NAMING) => {
  if (isSkippedCell(row, col, naming)) {
    return `${getRowLabel(row, naming)}·`;
  }
  return `${getRowLabel(row, naming)}${getSeatNumberForCol(col, naming, row)}`;
};

/** Prefer manual name when seat.customLabel is set. */
export const displaySeatLabel = (seat, naming = DEFAULT_NAMING) => {
  if (!seat) return '';
  if (seat.customLabel && seat.label) return String(seat.label);
  if (Number.isFinite(Number(seat.row)) && Number.isFinite(Number(seat.col))) {
    return formatSeatLabel(Number(seat.row), Number(seat.col), naming);
  }
  return seat.label || '';
};

/** Normalize API seats for the editor (row/col numbers + customLabel flag). */
export const hydrateSeatsFromApi = (seats = []) =>
  (seats || []).map((seat, index) => {
    const meta = seat.metadata && typeof seat.metadata === 'object' ? seat.metadata : {};
    const customLabel = seat.customLabel === true || meta.customLabel === true;
    const col =
      seat.col != null && seat.col !== ''
        ? Number(seat.col)
        : Number.isFinite(Number(seat.x))
          ? Math.round(Number(seat.x) / GRID_SIZE)
          : index;
    let row = seat.row;
    if (row != null && row !== '' && Number.isFinite(Number(row))) {
      row = Number(row);
    } else if (Number.isFinite(Number(seat.y))) {
      row = Math.round(Number(seat.y) / GRID_SIZE);
    } else {
      row = 0;
    }
    return {
      ...seat,
      row,
      col,
      x: Number.isFinite(Number(seat.x)) ? Number(seat.x) : col * GRID_SIZE,
      y: Number.isFinite(Number(seat.y)) ? Number(seat.y) : row * GRID_SIZE,
      customLabel,
      metadata: { ...meta, customLabel },
    };
  });

export const relabelSeats = (seats, naming = DEFAULT_NAMING) =>
  (seats || []).map((seat) => {
    const voided = isSkippedCell(seat.row, seat.col, naming);
    const keepCustom = !!seat.customLabel && !!seat.label;
    return {
      ...seat,
      label: keepCustom ? seat.label : formatSeatLabel(seat.row, seat.col, naming),
      number: voided ? seat.number : getSeatNumberForCol(seat.col, naming, seat.row),
      isActive: voided ? false : seat.isActive !== false,
      seatType: voided ? seat.seatType || 'RESTRICTED' : seat.seatType,
    };
  });

export const toggleInList = (list, value) => {
  const v = Number(value);
  const arr = [...(list || [])];
  const i = arr.indexOf(v);
  if (i >= 0) arr.splice(i, 1);
  else arr.push(v);
  return arr.sort((a, b) => a - b);
};

export const getGridPosition = (
  clientX,
  clientY,
  gridRef,
  zoom,
  _viewOffset, // kept for call-site compat; NOT used (rect already includes translate)
  gridCols,
  gridRows = GRID_ROWS,
) => {
  const canvas = clientToCanvasCoords(clientX, clientY, gridRef, zoom);
  if (!canvas) return null;

  const col = Math.floor(canvas.x / GRID_SIZE);
  const row = Math.floor(canvas.y / GRID_SIZE);

  if (col >= 0 && col < gridCols && row >= 0 && row < gridRows) {
    return { row, col, x: canvas.x, y: canvas.y };
  }
  return null;
};

/**
 * Convert screen mouse coords → canvas content coords.
 * gridRef MUST be the element that has transform: translate() scale().
 * getBoundingClientRect() already includes translate, so only divide by zoom.
 */
export const clientToCanvasCoords = (clientX, clientY, gridRef, zoom = 1) => {
  if (!gridRef?.current) return null;
  const rect = gridRef.current.getBoundingClientRect();
  const z = zoom || 1;
  return {
    x: (clientX - rect.left) / z,
    y: (clientY - rect.top) / z,
  };
};

export const getSeatAtPosition = (seats, row, col) => {
  return seats.find((seat) => seat.row === row && seat.col === col);
};

export const getSeatAtPositionWithDetached = (seats, row, col) => {
  const exactMatch = seats.find((seat) => seat.row === row && seat.col === col);
  if (exactMatch) return exactMatch;

  const detachedSeat = seats.find((seat) => {
    if (!seat.isDetached) return false;
    const minRow = seat.row - 0.5;
    const maxRow = seat.row + 0.5;
    const minCol = seat.col - 0.5;
    const maxCol = seat.col + 0.5;
    return row >= minRow && row < maxRow && col >= minCol && col < maxCol;
  });

  return detachedSeat || null;
};

export const pointInPolygon = (x, y, points = []) => {
  if (!points || points.length < 3) return false;
  let inside = false;
  for (let i = 0, j = points.length - 1; i < points.length; j = i++) {
    const xi = points[i].x;
    const yi = points[i].y;
    const xj = points[j].x;
    const yj = points[j].y;
    const intersect =
      yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi + Number.EPSILON) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
};

export const getZonePolygonPoints = (zone) => {
  if (!zone) return null;
  if (Array.isArray(zone.polygonPoints) && zone.polygonPoints.length >= 3) {
    return zone.polygonPoints;
  }
  if (Array.isArray(zone.points) && zone.points.length >= 3) {
    return zone.points;
  }
  return null;
};

/** True if pixel (x,y) is inside the zone's real shape (rect / circle / polygon). */
export const isPositionInZone = (x, y, zone) => {
  if (!zone) return false;

  const poly = getZonePolygonPoints(zone);
  if (
    zone.type === 'POLYGON' ||
    (poly && poly.length >= 3 && zone.type !== 'CIRCLE' && !zone.isCircle)
  ) {
    if (poly && poly.length >= 3) return pointInPolygon(x, y, poly);
  }

  const zx = Number(zone.x) || 0;
  const zy = Number(zone.y) || 0;
  const zw = Number(zone.width) || 0;
  const zh = Number(zone.height) || 0;
  if (zw <= 0 || zh <= 0) return false;

  if (zone.isCircle || zone.type === 'CIRCLE') {
    const centerX = zx + zw / 2;
    const centerY = zy + zh / 2;
    const radius = Math.min(zw, zh) / 2;
    const dx = x - centerX;
    const dy = y - centerY;
    return dx * dx + dy * dy <= radius * radius;
  }

  return x >= zx && x <= zx + zw && y >= zy && y <= zy + zh;
};

/** Grid cell center in zone? */
export const isCellInZone = (row, col, zone) => {
  const cx = col * GRID_SIZE + GRID_SIZE / 2;
  const cy = row * GRID_SIZE + GRID_SIZE / 2;
  return isPositionInZone(cx, cy, zone);
};

/**
 * How many seat slots fit in a zone (grid cells inside shape, skipping aisle/void).
 */
export const countZoneSeatSlots = (
  zone,
  gridCols,
  gridRows,
  naming = DEFAULT_NAMING,
  { rowGap = 1, colGap = 1 } = {},
) => {
  if (!zone || zone.isStage) return 0;

  const poly = getZonePolygonPoints(zone);
  let minCol;
  let maxCol;
  let minRow;
  let maxRow;

  if (poly && poly.length >= 3) {
    const xs = poly.map((p) => p.x);
    const ys = poly.map((p) => p.y);
    minCol = Math.max(0, Math.floor(Math.min(...xs) / GRID_SIZE));
    maxCol = Math.min(gridCols - 1, Math.floor(Math.max(...xs) / GRID_SIZE));
    minRow = Math.max(0, Math.floor(Math.min(...ys) / GRID_SIZE));
    maxRow = Math.min(gridRows - 1, Math.floor(Math.max(...ys) / GRID_SIZE));
  } else {
    const zx = Number(zone.x) || 0;
    const zy = Number(zone.y) || 0;
    const zw = Number(zone.width) || 0;
    const zh = Number(zone.height) || 0;
    if (zw <= 0 || zh <= 0) return 0;
    minCol = Math.max(0, Math.floor(zx / GRID_SIZE));
    maxCol = Math.min(gridCols - 1, Math.floor((zx + zw) / GRID_SIZE));
    minRow = Math.max(0, Math.floor(zy / GRID_SIZE));
    maxRow = Math.min(gridRows - 1, Math.floor((zy + zh) / GRID_SIZE));
  }

  let count = 0;
  for (let row = minRow; row <= maxRow; row += rowGap) {
    if (isSkippedRow(row, naming)) continue;
    for (let col = minCol; col <= maxCol; col += colGap) {
      if (isSkippedCol(col, naming) || isAisleCell(row, col, naming)) continue;
      if (isCellInZone(row, col, zone)) count += 1;
    }
  }
  return count;
};

/**
 * Split layout target capacity equally across seat zones, then grow/shrink
 * each zone's geometry so it can hold that many seats.
 */
export const getZoneCentroid = (zone) => {
  const poly = getZonePolygonPoints(zone);
  if (poly && poly.length >= 3) {
    return {
      cx: poly.reduce((s, p) => s + p.x, 0) / poly.length,
      cy: poly.reduce((s, p) => s + p.y, 0) / poly.length,
    };
  }
  const zx = Number(zone.x) || 0;
  const zy = Number(zone.y) || 0;
  const zw = Number(zone.width) || 0;
  const zh = Number(zone.height) || (zone.isCircle ? zw : 0);
  return { cx: zx + zw / 2, cy: zy + (zh || zw) / 2 };
};

export const zoneBoundingBox = (zone) => {
  const poly = getZonePolygonPoints(zone);
  if (poly && poly.length >= 3) {
    const xs = poly.map((p) => p.x);
    const ys = poly.map((p) => p.y);
    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const minY = Math.min(...ys);
    const maxY = Math.max(...ys);
    return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
  }
  return {
    x: Number(zone.x) || 0,
    y: Number(zone.y) || 0,
    width: Number(zone.width) || 0,
    height: Number(zone.height) || (zone.isCircle ? Number(zone.width) || 0 : 0),
  };
};

/**
 * Scale zone about its centroid. When clamp=false, may extend past grid
 * (caller can expand grid afterwards).
 */
export const scaleZoneGeometry = (
  zone,
  scale,
  gridCols = DEFAULT_GRID_COLS,
  gridRows = GRID_ROWS,
  { clamp = true } = {},
) => {
  if (!zone || !Number.isFinite(scale) || scale <= 0 || Math.abs(scale - 1) < 0.001) {
    return zone;
  }
  const { cx, cy } = getZoneCentroid(zone);
  const maxW = gridCols * GRID_SIZE;
  const maxH = gridRows * GRID_SIZE;
  const clampPt = (x, y) =>
    clamp
      ? {
          x: Math.max(0, Math.min(maxW, x)),
          y: Math.max(0, Math.min(maxH, y)),
        }
      : { x: Math.max(0, x), y: Math.max(0, y) };

  const poly = getZonePolygonPoints(zone);
  if (poly && poly.length >= 3) {
    const points = poly.map((p) => clampPt(cx + (p.x - cx) * scale, cy + (p.y - cy) * scale));
    const box = {
      x: Math.min(...points.map((p) => p.x)),
      y: Math.min(...points.map((p) => p.y)),
      width: 0,
      height: 0,
    };
    box.width = Math.max(...points.map((p) => p.x)) - box.x;
    box.height = Math.max(...points.map((p) => p.y)) - box.y;
    return {
      ...zone,
      ...box,
      polygonPoints: points,
      ...(Array.isArray(zone.points) ? { points } : {}),
    };
  }

  const zw = Math.max(GRID_SIZE, Number(zone.width) || GRID_SIZE);
  const zh = Math.max(
    GRID_SIZE,
    Number(zone.height) || (zone.isCircle || zone.type === 'CIRCLE' ? zw : GRID_SIZE),
  );
  let newW = Math.max(GRID_SIZE, zw * scale);
  let newH = zone.isCircle || zone.type === 'CIRCLE' ? newW : Math.max(GRID_SIZE, zh * scale);
  let newX = cx - newW / 2;
  let newY = cy - newH / 2;
  if (clamp) {
    newX = Math.max(0, Math.min(Math.max(0, maxW - newW), newX));
    newY = Math.max(0, Math.min(Math.max(0, maxH - newH), newY));
    newW = Math.min(newW, maxW - newX);
    newH = Math.min(newH, maxH - newY);
    if (zone.isCircle || zone.type === 'CIRCLE') {
      const side = Math.min(newW, newH);
      newW = side;
      newH = side;
    }
  } else {
    newX = Math.max(0, newX);
    newY = Math.max(0, newY);
  }
  return { ...zone, x: newX, y: newY, width: newW, height: newH };
};

/**
 * Grow/shrink zone until seat slots ≈ targetSeats (about centroid).
 */
export const scaleZoneToFitCapacity = (
  zone,
  targetSeats,
  gridCols,
  gridRows,
  naming = DEFAULT_NAMING,
) => {
  const goal = Math.max(1, Math.floor(Number(targetSeats) || 0));
  let z = { ...zone };
  let slots = countZoneSeatSlots(z, gridCols, gridRows, naming);

  if (slots <= 0) {
    // Ensure a minimal footprint then grow
    z = {
      ...z,
      width: Math.max(Number(z.width) || 0, GRID_SIZE * 4),
      height: Math.max(Number(z.height) || 0, GRID_SIZE * 4),
    };
    slots = countZoneSeatSlots(z, gridCols * 2, gridRows * 2, naming) || 1;
  }

  // Area ~ seats → linear scale ≈ sqrt
  let scale = Math.sqrt(goal / Math.max(slots, 1));
  scale = Math.max(0.35, Math.min(scale, 12));
  z = scaleZoneGeometry(z, scale, gridCols, gridRows, { clamp: false });
  slots = countZoneSeatSlots(
    z,
    Math.max(gridCols, Math.ceil(((z.x || 0) + (z.width || 0)) / GRID_SIZE) + 2),
    Math.max(gridRows, Math.ceil(((z.y || 0) + (z.height || 0)) / GRID_SIZE) + 2),
    naming,
  );

  for (let i = 0; i < 14; i += 1) {
    if (slots >= goal && slots <= goal * 1.1) break;
    const factor = slots < goal ? 1.07 : 0.95;
    const next = scaleZoneGeometry(z, factor, gridCols, gridRows, { clamp: false });
    const bigCols = Math.max(
      gridCols,
      Math.ceil(((next.x || 0) + (next.width || 0)) / GRID_SIZE) + 4,
    );
    const bigRows = Math.max(
      gridRows,
      Math.ceil(((next.y || 0) + (next.height || 0)) / GRID_SIZE) + 4,
    );
    const nextSlots = countZoneSeatSlots(next, bigCols, bigRows, naming);
    if (nextSlots === slots && factor > 1) {
      // Stuck growing — nudge size explicitly
      z = {
        ...next,
        width: (next.width || GRID_SIZE) * 1.08,
        height: (next.height || GRID_SIZE) * 1.08,
      };
      if (Array.isArray(next.polygonPoints) && next.polygonPoints.length) {
        z = scaleZoneGeometry(next, 1.08, gridCols, gridRows, { clamp: false });
      }
      slots = countZoneSeatSlots(
        z,
        Math.max(bigCols, Math.ceil(((z.x || 0) + (z.width || 0)) / GRID_SIZE) + 4),
        Math.max(bigRows, Math.ceil(((z.y || 0) + (z.height || 0)) / GRID_SIZE) + 4),
        naming,
      );
      continue;
    }
    z = next;
    slots = nextSlots;
  }

  return { ...z, capacity: goal };
};

/**
 * Equal-split target across seat zones and resize each zone to fit its share.
 * Returns { zones, gridCols, gridRows }.
 */
export const distributeTargetCapacity = (
  zones,
  target,
  gridCols,
  gridRows,
  naming = DEFAULT_NAMING,
) => {
  const goal = Math.max(0, Math.floor(Number(target) || 0));
  const list = zones || [];
  const seatZones = list.filter((z) => z && !z.isStage && z.name !== 'SAHNE');
  if (!seatZones.length || !goal) {
    return { zones: list, gridCols, gridRows };
  }

  const n = seatZones.length;
  const base = Math.floor(goal / n);
  let rem = goal - base * n;
  const assigned = {};
  seatZones.forEach((z) => {
    assigned[z.id] = base + (rem > 0 ? 1 : 0);
    if (rem > 0) rem -= 1;
  });

  // Work on a generous virtual grid while sizing
  let workCols = Math.max(gridCols, 40);
  let workRows = Math.max(gridRows, 40);

  const sized = list.map((z) => {
    if (assigned[z.id] == null) return z;
    return scaleZoneToFitCapacity(z, assigned[z.id], workCols, workRows, naming);
  });

  // Expand real grid to contain all zones (+ padding)
  let needCols = gridCols;
  let needRows = gridRows;
  sized.forEach((z) => {
    if (!z || z.isStage) return;
    const box = zoneBoundingBox(z);
    needCols = Math.max(needCols, Math.ceil((box.x + box.width) / GRID_SIZE) + 2);
    needRows = Math.max(needRows, Math.ceil((box.y + box.height) / GRID_SIZE) + 2);
  });

  needCols = Math.min(Math.max(needCols, gridCols), MAX_GRID_COLS);
  needRows = Math.min(Math.max(needRows, gridRows), MAX_GRID_ROWS);

  return { zones: sized, gridCols: needCols, gridRows: needRows };
};

export const sumZoneCapacities = (zones = []) =>
  zones
    .filter((z) => z && !z.isStage && z.name !== 'SAHNE')
    .reduce((sum, z) => sum + (Number(z.capacity) || 0), 0);

export const sumZoneMaxSlots = (zones, gridCols, gridRows, naming = DEFAULT_NAMING) =>
  (zones || [])
    .filter((z) => z && !z.isStage && z.name !== 'SAHNE')
    .reduce((sum, z) => sum + countZoneSeatSlots(z, gridCols, gridRows, naming), 0);

export const detectZoneForPosition = (x, y, zones = []) => {
  if (!zones || zones.length === 0) return null;
  // Last drawn / higher sortOrder wins if overlapping
  const ordered = [...zones].sort(
    (a, b) => (Number(a.sortOrder) || 0) - (Number(b.sortOrder) || 0),
  );
  let hit = null;
  for (const zone of ordered) {
    if (isPositionInZone(x, y, zone)) hit = zone;
  }
  return hit;
};

export const isPositionOccupied = (seats, row, col, excludeSeatKeys = []) => {
  const exactMatch = seats.find((seat) => {
    const seatKey = `${seat.row}-${seat.col}`;
    return seat.row === row && seat.col === col && !excludeSeatKeys.includes(seatKey);
  });
  if (exactMatch) return true;

  const detachedConflict = seats.find((seat) => {
    const seatKey = `${seat.row}-${seat.col}`;
    if (!seat.isDetached || excludeSeatKeys.includes(seatKey)) return false;
    const minRow = seat.row - 0.5;
    const maxRow = seat.row + 0.5;
    const minCol = seat.col - 0.5;
    const maxCol = seat.col + 0.5;
    return row >= minRow && row < maxRow && col >= minCol && col < maxCol;
  });

  return !!detachedConflict;
};

export const makeSeat = ({
  row,
  col,
  seatType = 'REGULAR',
  number = 1,
  zoneId = null,
  naming = DEFAULT_NAMING,
}) => ({
  id: `seat-${Date.now()}-${row}-${col}-${Math.random().toString(36).slice(2, 8)}`,
  row,
  col,
  x: col * GRID_SIZE,
  y: row * GRID_SIZE,
  label: formatSeatLabel(row, col, naming),
  seatType,
  number: getSeatNumberForCol(col, naming),
  width: 1.0,
  height: 1.0,
  rotation: 0.0,
  accessibility: seatType === 'WHEELCHAIR',
  isActive: true,
  zoneId,
});

/**
 * Fill every empty grid cell whose center lies inside the zone shape.
 * Options: { seatType, rowGap, colGap, maxSeats }
 */
export const generateSeatsInZone = (zone, seats, gridCols, gridRows, options = {}) => {
  if (!zone) return [];
  const { seatType = 'REGULAR', rowGap = 1, colGap = 1, maxSeats = 50000 } = options;

  const zx = Number(zone.x) || 0;
  const zy = Number(zone.y) || 0;
  const zw = Number(zone.width) || 0;
  const zh = Number(zone.height) || 0;

  const minCol = Math.max(0, Math.floor(zx / GRID_SIZE));
  const maxCol = Math.min(gridCols - 1, Math.floor((zx + zw) / GRID_SIZE));
  const minRow = Math.max(0, Math.floor(zy / GRID_SIZE));
  const maxRow = Math.min(gridRows - 1, Math.floor((zy + zh) / GRID_SIZE));

  const newSeats = [];
  for (let row = minRow; row <= maxRow; row += rowGap) {
    for (let col = minCol; col <= maxCol; col += colGap) {
      if (newSeats.length >= maxSeats) return newSeats;
      if (!isCellInZone(row, col, zone)) continue;
      if (isPositionOccupied(seats, row, col)) continue;
      // Also skip if overlapping a seat we're about to add
      if (newSeats.some((s) => s.row === row && s.col === col)) continue;

      newSeats.push(
        makeSeat({
          row,
          col,
          seatType,
          number: seats.length + newSeats.length + 1,
          zoneId: zone.id,
        }),
      );
    }
  }
  return newSeats;
};

/** Dense tribune block inside zone AABB, clipped to shape. */
export const generateTribuneBlock = (
  zone,
  seats,
  gridCols,
  gridRows,
  { rows = 12, seatsPerRow = 24, seatType = 'REGULAR' } = {},
) => {
  if (!zone) return [];
  const zx = Number(zone.x) || 0;
  const zy = Number(zone.y) || 0;
  const zw = Number(zone.width) || 0;
  const zh = Number(zone.height) || 0;

  const startRow = Math.max(0, Math.floor(zy / GRID_SIZE) + 1);
  const startCol = Math.max(0, Math.floor(zx / GRID_SIZE) + 1);
  const maxRows = Math.min(rows, Math.floor(zh / GRID_SIZE) - 2, gridRows - startRow);
  const maxCols = Math.min(seatsPerRow, Math.floor(zw / GRID_SIZE) - 2, gridCols - startCol);

  const newSeats = [];
  for (let r = 0; r < maxRows; r += 1) {
    for (let c = 0; c < maxCols; c += 1) {
      const row = startRow + r;
      const col = startCol + c;
      if (!isCellInZone(row, col, zone)) continue;
      if (isPositionOccupied(seats, row, col)) continue;
      if (newSeats.some((s) => s.row === row && s.col === col)) continue;
      newSeats.push(
        makeSeat({
          row,
          col,
          seatType,
          number: seats.length + newSeats.length + 1,
          zoneId: zone.id,
        }),
      );
    }
  }
  return newSeats;
};

export const translatePolygonPoints = (points, dx, dy) => {
  if (!Array.isArray(points)) return points;
  return points.map((p) => ({ x: p.x + dx, y: p.y + dy }));
};

export const scalePolygonPoints = (points, oldZone, newZone) => {
  if (!Array.isArray(points) || points.length === 0) return points;
  const ox = Number(oldZone.x) || 0;
  const oy = Number(oldZone.y) || 0;
  const ow = Number(oldZone.width) || 1;
  const oh = Number(oldZone.height) || 1;
  const nx = Number(newZone.x) || 0;
  const ny = Number(newZone.y) || 0;
  const nw = Number(newZone.width) || 1;
  const nh = Number(newZone.height) || 1;
  return points.map((p) => ({
    x: nx + ((p.x - ox) / ow) * nw,
    y: ny + ((p.y - oy) / oh) * nh,
  }));
};

export const addTheaterLayout = (
  seats,
  setSeats,
  onSeatsCreate,
  saveToHistory,
  getSeatAtPositionFn,
  gridCols,
  selectedZone = null,
  gridRows = GRID_ROWS,
) => {
  if (selectedZone) {
    const block = generateTribuneBlock(selectedZone, seats, gridCols, gridRows, {
      rows: 10,
      seatsPerRow: 18,
    });
    if (block.length === 0) return;
    const updatedSeats = [...seats, ...block];
    setSeats(updatedSeats);
    onSeatsCreate(updatedSeats);
    saveToHistory(updatedSeats, `Tribün düzeni eklendi (${block.length} koltuk)`);
    return;
  }

  const layouts = [
    { row: 3, seats: 8 },
    { row: 4, seats: 10 },
    { row: 5, seats: 12 },
    { row: 7, seats: 14 },
    { row: 8, seats: 16 },
    { row: 9, seats: 18 },
    { row: 10, seats: 18 },
    { row: 11, seats: 20 },
    { row: 12, seats: 20 },
  ];

  const allNewSeats = [];
  layouts.forEach((layout) => {
    if (layout.row >= gridRows) return;
    const startCol = Math.floor((gridCols - layout.seats) / 2);
    for (let i = 0; i < layout.seats; i++) {
      const col = startCol + i;
      if (col >= 0 && col < gridCols && !getSeatAtPositionFn(seats, layout.row, col)) {
        allNewSeats.push(
          makeSeat({
            row: layout.row,
            col,
            number: seats.length + allNewSeats.length + 1,
            zoneId: null,
          }),
        );
      }
    }
  });

  const updatedSeats = [...seats, ...allNewSeats];
  setSeats(updatedSeats);
  onSeatsCreate(updatedSeats);
  saveToHistory(updatedSeats, `Tiyatro düzeni eklendi (${allNewSeats.length} koltuk)`);
};

export const addRowFromPosition = (
  seats,
  setSeats,
  onSeatsCreate,
  saveToHistory,
  getSeatAtPositionFn,
  startRow,
  startCol,
  seatsCount = 10,
  gridCols,
  selectedZone = null,
  gridRows = GRID_ROWS,
) => {
  const newSeats = [];

  for (let i = 0; i < seatsCount; i++) {
    const col = startCol + i;
    const row = startRow;
    if (col < 0 || col >= gridCols || row < 0 || row >= gridRows) continue;
    if (selectedZone && !isCellInZone(row, col, selectedZone)) continue;
    if (getSeatAtPositionFn(seats, row, col)) continue;

    newSeats.push(
      makeSeat({
        row,
        col,
        number: seats.length + newSeats.length + 1,
        zoneId: selectedZone?.id || null,
      }),
    );
  }

  if (newSeats.length > 0) {
    const updatedSeats = [...seats, ...newSeats];
    setSeats(updatedSeats);
    onSeatsCreate(updatedSeats);
    saveToHistory(updatedSeats, `Pozisyondan sıra eklendi (${newSeats.length} koltuk)`);
  }
};
